import '../../modal/components/modal/modal.component.scss';
import './membership-modal-extensions.scss';

import type { FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { useTranslation } from '../../i18n';
import { useAppSelector } from '../../store/hooks';
import type { Contract, MembershipContractAddressUpdateRequest } from '../models/membership.model';
import { selectAddressUpdating } from '../store/membership.selectors';

export interface MembershipAddressModalProps {
  contract: Contract;
  countryOptions: string; // HTML string with country options
  onClose: (address: MembershipContractAddressUpdateRequest | null) => void;
}

export const MembershipAddressModal: FunctionComponent<MembershipAddressModalProps> = ({
  contract,
  countryOptions,
  onClose,
}) => {
  const countryRef = useRef<HTMLSelectElement>(null);
  const provinceRef = useRef<HTMLSelectElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const addressUpdating = useAppSelector(selectAddressUpdating);
  const { t } = useTranslation();

  // Form state
  const [address, setAddress] = useState<MembershipContractAddressUpdateRequest>({
    contractId: contract.id,
    sourceContractId: contract.sourceContractId,
    streetLine1: contract.streetLine1 || '',
    streetLine2: contract.streetLine2 || '',
    city: contract.city || '',
    country: contract.country || '',
    firstName: contract.firstName || '',
    lastName: contract.lastName || '',
    province: contract.province || '',
    zipCode: contract.zipCode || '',
    phoneNumber: contract.phoneNumber || '',
  });

  const [displayProvince, setDisplayProvince] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update province dropdown based on selected country
  const updateProvinces = useCallback((countryCode: string, provinceToPreserve?: string) => {
    if (!countryRef.current) return;

    // Find the selected country option
    const selectedOption = countryRef.current.querySelector(
      `option[value="${countryCode}"]`
    ) as HTMLOptionElement;

    if (selectedOption?.dataset.provinces) {
      try {
        const provinces = JSON.parse(selectedOption.dataset.provinces) as [string, string][];

        if (provinces.length > 0) {
          setDisplayProvince(true);

          // Check if the province to preserve exists in the options
          const provinceValues = provinces.map(([, value]) => value);
          const validProvince =
            provinceToPreserve && provinceValues.includes(provinceToPreserve)
              ? provinceToPreserve
              : provinces[0][1];

          setAddress((prev) => ({ ...prev, province: validProvince }));

          // Populate province options after displayProvince is set
          setTimeout(() => {
            if (provinceRef.current) {
              provinceRef.current.innerHTML = '';
              provinces.forEach(([label, value]) => {
                const option = document.createElement('option');
                option.value = value;
                option.textContent = label;
                provinceRef.current?.appendChild(option);
              });
              provinceRef.current.value = validProvince;
            }
          }, 0);
        } else {
          setDisplayProvince(false);
          setAddress((prev) => ({ ...prev, province: '' }));
        }
      } catch (e) {
        console.error('Failed to parse provinces:', e);
        setDisplayProvince(false);
      }
    } else {
      setDisplayProvince(false);
      setAddress((prev) => ({ ...prev, province: '' }));
    }
  }, []);

  // Initialize provinces on mount
  useEffect(() => {
    if (contract.country && countryRef.current) {
      updateProvinces(contract.country, contract.province);
    }
  }, [contract.country, contract.province, countryOptions, updateProvinces]);

  // Reset submission flag when update completes (success or failure)
  useEffect(() => {
    if (!addressUpdating) {
      isSubmittingRef.current = false;
    }
  }, [addressUpdating]);

  // Form field change handler
  const handleFieldChange = useCallback(
    (field: keyof MembershipContractAddressUpdateRequest, value: string) => {
      setAddress((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
      // Handle country change
      if (field === 'country') {
        updateProvinces(value);
      }
    },
    [errors, updateProvinces]
  );

  // Mark field as touched
  const handleFieldBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Validate form
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    if (!address.streetLine1?.trim()) {
      newErrors.streetLine1 = t('address.errorStreetRequired');
    }
    if (!address.country?.trim()) {
      newErrors.country = t('address.errorCountryRequired');
    }
    if (!address.zipCode?.trim()) {
      newErrors.zipCode = t('address.errorZipRequired');
    }
    if (!address.phoneNumber?.trim()) {
      newErrors.phoneNumber = t('address.errorPhoneRequired');
    }
    setErrors(newErrors);
    // Mark all required fields as touched
    setTouched({
      streetLine1: true,
      country: true,
      zipCode: true,
      phoneNumber: true,
    });
    return Object.keys(newErrors).length === 0;
  }, [address]);

  // Submit handler
  const handleSubmit = useCallback(
    (e: Event) => {
      e.preventDefault();

      // Prevent duplicate submissions
      if (isSubmittingRef.current) {
        return;
      }

      if (validate()) {
        isSubmittingRef.current = true;
        onClose(address);
      }
    },
    [address, validate, onClose]
  );

  // Cancel handler
  const handleCancel = useCallback(() => {
    onClose(null);
  }, [onClose]);

  return (
    <div className="single-membership-address-modal">
      <div className="single-modal-header">
        <h3 className="single-modal-title">{t('address.updateAddress')}</h3>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="single-modal-body single-membership-address-form">
          {/* First Name & Last Name Row */}
          <div className="single-membership-input-row">
            <div className="single-membership-input-group">
              <label htmlFor="first-name">{t('address.firstName')}</label>
              <input
                id="first-name"
                type="text"
                value={address.firstName}
                onChange={(e) =>
                  handleFieldChange('firstName', (e.target as HTMLInputElement).value)
                }
                onBlur={() => handleFieldBlur('firstName')}
              />
            </div>
            <div className="single-membership-input-group">
              <label htmlFor="last-name">{t('address.lastName')}</label>
              <input
                id="last-name"
                type="text"
                value={address.lastName}
                onChange={(e) =>
                  handleFieldChange('lastName', (e.target as HTMLInputElement).value)
                }
                onBlur={() => handleFieldBlur('lastName')}
              />
            </div>
          </div>
          {/* Street Line 1 */}
          <div className="single-membership-input-group">
            <label htmlFor="street-1" className="required">
              {t('address.street')}
            </label>
            <input
              id="street-1"
              type="text"
              value={address.streetLine1}
              onChange={(e) =>
                handleFieldChange('streetLine1', (e.target as HTMLInputElement).value)
              }
              onBlur={() => handleFieldBlur('streetLine1')}
              className={touched.streetLine1 && errors.streetLine1 ? 'error' : ''}
              required
            />
            {touched.streetLine1 && errors.streetLine1 && (
              <span className="single-membership-error-message">{errors.streetLine1}</span>
            )}
          </div>
          {/* Street Line 2 */}
          <div className="single-membership-input-group">
            <label htmlFor="street-2">{t('address.streetLine2')}</label>
            <input
              id="street-2"
              type="text"
              value={address.streetLine2}
              onChange={(e) =>
                handleFieldChange('streetLine2', (e.target as HTMLInputElement).value)
              }
            />
          </div>
          {/* City & Country Row */}
          <div className="single-membership-input-row">
            <div className="single-membership-input-group">
              <label htmlFor="city">{t('address.city')}</label>
              <input
                id="city"
                type="text"
                value={address.city}
                onChange={(e) => handleFieldChange('city', (e.target as HTMLInputElement).value)}
              />
            </div>
            <div className="single-membership-input-group">
              <label htmlFor="country" className="required">
                {t('address.country')}
              </label>
              <select
                id="country"
                ref={countryRef}
                value={address.country}
                onChange={(e) =>
                  handleFieldChange('country', (e.target as HTMLSelectElement).value)
                }
                onBlur={() => handleFieldBlur('country')}
                className={touched.country && errors.country ? 'error' : ''}
                dangerouslySetInnerHTML={{ __html: countryOptions }}
                required
              />
              {touched.country && errors.country && (
                <span className="single-membership-error-message">{errors.country}</span>
              )}
            </div>
          </div>
          {/* Province */}
          {displayProvince && (
            <div className="single-membership-input-group">
              <label htmlFor="province">{t('address.province')}</label>
              <select
                id="province"
                ref={provinceRef}
                value={address.province}
                onChange={(e) =>
                  handleFieldChange('province', (e.target as HTMLSelectElement).value)
                }
              />
            </div>
          )}
          {/* Zip Code */}
          <div className="single-membership-input-group">
            <label htmlFor="zip" className="required">
              {t('address.zip')}
            </label>
            <input
              id="zip"
              type="text"
              value={address.zipCode}
              onChange={(e) => handleFieldChange('zipCode', (e.target as HTMLInputElement).value)}
              onBlur={() => handleFieldBlur('zipCode')}
              className={touched.zipCode && errors.zipCode ? 'error' : ''}
              required
            />
            {touched.zipCode && errors.zipCode && (
              <span className="single-membership-error-message">{errors.zipCode}</span>
            )}
          </div>
          {/* Phone Number */}
          <div className="single-membership-input-group">
            <label htmlFor="phone-number" className="required">
              {t('address.phoneNumber')}
            </label>
            <input
              id="phone-number"
              type="tel"
              value={address.phoneNumber}
              onChange={(e) =>
                handleFieldChange('phoneNumber', (e.target as HTMLInputElement).value)
              }
              onBlur={() => handleFieldBlur('phoneNumber')}
              className={touched.phoneNumber && errors.phoneNumber ? 'error' : ''}
              required
            />
            {touched.phoneNumber && errors.phoneNumber && (
              <span className="single-membership-error-message">{errors.phoneNumber}</span>
            )}
          </div>
        </div>
        <div className="single-modal-footer single-membership-separator inline--btns">
          <button type="button" className="btn button btn-outline" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={addressUpdating} className="btn button">
            {addressUpdating ? t('address.updating') : t('address.update')}
          </button>
        </div>
      </form>
    </div>
  );
};
